// Ready-made module designs offered on the builder's Preview tab. Each one is
// a complete module definition, the same shape the server returns, so using a
// template is the same as cloning a module. Templates differ in layout on
// purpose: column counts, boards, add-more rows, uploads, hidden fields,
// conditional fields and button flows, so most needs start close to one.

const GREEN = "#17B65B";
const BLUE = "#3D4AF5";
const AMBER = "#D98E04";
const RED = "#D61916";
const GREY = "#546088";
const PURPLE = "#6929E2";

const field = (key, label, type, section, extra = {}) => ({ key, label, type, section, ...extra });
const choice = (key, label, section, options, colors = [], extra = {}) =>
  field(key, label, "select", section, {
    options,
    option_colors: Object.fromEntries(options.map((option, i) => [option, colors[i]]).filter(([, color]) => color)),
    ...extra,
  });
const button = (label, fieldKey, value, color, extra = {}) => ({ label, field: fieldKey, value, color, confirm: null, visible_when: null, ...extra });
const when = (fieldKey, value) => ({ field: fieldKey, value });

export const MODULE_TEMPLATES = [
  {
    key: "vendors",
    name: "Vendor Onboarding",
    description: "Suppliers with contacts, tax details and documents, approved through hidden status buttons.",
    module: {
      name: "Vendors",
      settings: {
        singular: "Vendor",
        description: "Suppliers we buy from",
        code_prefix: "VEN-",
        sections: [
          { key: "basic", title: "Basic Info", columns: 3 },
          { key: "contact", title: "Contact", columns: 2 },
          { key: "compliance", title: "Tax & Documents", columns: 2 },
        ],
        list: { columns: ["vendor_name", "category", "status", "contact_person", "email"], filters: ["category", "status"], sort_field: "", sort_dir: "desc", page_size: 10, board_field: "status" },
        view: { history: true, related: true },
      },
      fields: [
        field("vendor_name", "Vendor Name", "text", "basic", { required: true, unique: true }),
        choice("category", "Category", "basic", ["Raw Material", "Services", "Logistics", "Others"], [BLUE, PURPLE, AMBER, GREY]),
        choice("status", "Status", "basic", ["Active", "On Hold", "Blacklisted"], [GREEN, AMBER, RED], { default: "Active" }),
        field("contact_person", "Contact Person", "text", "contact"),
        field("email", "Email", "email", "contact"),
        field("phones", "Mobile Number", "repeater", "contact", {
          width: "full",
          max_rows: 3,
          subfields: [
            { key: "number", label: "Mobile number", type: "phone", required: true },
            { key: "kind", label: "Type", type: "select", options: ["Primary", "Secondary"] },
          ],
        }),
        field("website", "Website", "url", "contact"),
        field("gst_number", "GST Number", "text", "compliance", { max_length: 15, help: "15 characters, Ex: 33ABCDE1234F1Z5" }),
        field("pan_number", "PAN", "text", "compliance", { max_length: 10 }),
        field("documents", "Documents", "attachment", "compliance", { width: "full", file_kind: "documents", max_files: 5, max_size_mb: 5 }),
        choice("approve_status", "Approve Status", "basic", ["Pending", "Approved", "Rejected"], [], { hidden: true, default: "Pending" }),
      ],
      actions: [
        button("Approve", "approve_status", "Approved", "success", { confirm: "Approve this vendor?", visible_when: when("approve_status", "Pending") }),
        button("Reject", "approve_status", "Rejected", "danger", { confirm: "Reject this vendor?", visible_when: when("approve_status", "Pending") }),
      ],
    },
  },
  {
    key: "assets",
    name: "Asset Register",
    description: "Company assets by serial number, who holds them, and warranty papers, on a status board.",
    module: {
      name: "Assets",
      settings: {
        singular: "Asset",
        description: "Laptops, phones, furniture and vehicles",
        code_prefix: "AST-",
        sections: [
          { key: "asset", title: "Asset Details", columns: 2 },
          { key: "assignment", title: "Assignment", columns: 2 },
          { key: "purchase", title: "Purchase & Warranty", columns: 3 },
        ],
        list: { columns: ["asset_name", "category", "serial_number", "status", "assigned_to"], filters: ["category", "status", "purchase_date"], sort_field: "", sort_dir: "desc", page_size: 20, board_field: "status" },
        view: { history: true, related: true },
      },
      fields: [
        field("asset_name", "Asset Name", "text", "asset", { required: true }),
        choice("category", "Category", "asset", ["Laptop", "Desktop", "Mobile", "Furniture", "Vehicle", "Other"]),
        field("serial_number", "Serial Number", "text", "asset", { unique: true }),
        choice("status", "Status", "asset", ["In Stock", "Assigned", "Under Repair", "Retired"], [BLUE, GREEN, AMBER, GREY], { default: "In Stock" }),
        field("notes", "Notes", "textarea", "asset", { width: "full" }),
        field("assigned_to", "Assigned To", "autocomplete", "assignment", { source: "employee", visible_when: when("status", "Assigned") }),
        field("assigned_on", "Assigned On", "date", "assignment", { visible_when: when("status", "Assigned") }),
        field("location", "Location", "text", "assignment"),
        field("purchase_date", "Purchase Date", "date", "purchase"),
        field("purchase_cost", "Purchase Cost", "number", "purchase", { min: 0 }),
        field("warranty_until", "Warranty Until", "date", "purchase"),
        field("invoice", "Invoice & Warranty Card", "attachment", "purchase", { width: "full", file_kind: "images_pdf", max_files: 2, max_size_mb: 5 }),
      ],
      actions: [
        button("Send for Repair", "status", "Under Repair", "warning"),
        button("Back in Stock", "status", "In Stock", "primary", { visible_when: when("status", "Under Repair") }),
        button("Retire", "status", "Retired", "secondary", { confirm: "Retire this asset? It stays on record." }),
      ],
    },
  },
  {
    key: "tickets",
    name: "Support Tickets",
    description: "Customer issues with priority and due dates, worked through a Start, Resolve, Close flow.",
    module: {
      name: "Support Tickets",
      settings: {
        singular: "Ticket",
        description: "Customer issues and requests",
        code_prefix: "TKT-",
        sections: [
          { key: "ticket", title: "Ticket", columns: 2 },
          { key: "details", title: "Details", columns: 1 },
        ],
        list: { columns: ["subject", "customer", "priority", "status", "assigned_to", "due_date"], filters: ["priority", "status", "customer"], sort_field: "due_date", sort_dir: "asc", page_size: 20, board_field: "status" },
        view: { history: true, related: true },
      },
      fields: [
        field("subject", "Subject", "text", "ticket", { required: true, width: "full" }),
        field("customer", "Customer", "autocomplete", "ticket", { required: true, source: "customer" }),
        choice("priority", "Priority", "ticket", ["Low", "Medium", "High", "Urgent"], [GREY, BLUE, AMBER, RED], { default: "Medium" }),
        choice("status", "Status", "ticket", ["Open", "In Progress", "Waiting on Customer", "Resolved", "Closed"], [BLUE, AMBER, PURPLE, GREEN, GREY], { default: "Open" }),
        field("assigned_to", "Assigned To", "autocomplete", "ticket", { source: "employee" }),
        choice("channel", "Channel", "ticket", ["Email", "Phone", "WhatsApp", "Walk-in"]),
        field("due_date", "Due Date", "date", "ticket"),
        field("description", "Description", "textarea", "details", { required: true }),
        field("screenshots", "Screenshots", "attachment", "details", { file_kind: "images_pdf", max_files: 5, max_size_mb: 5 }),
        field("resolution", "Resolution Notes", "textarea", "details", { visible_when: when("status", "Resolved") }),
      ],
      actions: [
        button("Start Work", "status", "In Progress", "primary", { visible_when: when("status", "Open") }),
        button("Resolve", "status", "Resolved", "success"),
        button("Close", "status", "Closed", "secondary", { confirm: "Close this ticket?", visible_when: when("status", "Resolved") }),
      ],
    },
  },
  {
    key: "expenses",
    name: "Expense Claims",
    description: "Employee claims with receipts, a billable-to-customer switch, and an approve and pay flow.",
    module: {
      name: "Expense Claims",
      settings: {
        singular: "Expense Claim",
        description: "Claims raised by the team",
        code_prefix: "EXP-",
        sections: [
          { key: "claim", title: "Claim", columns: 2 },
          { key: "proof", title: "Receipts", columns: 1 },
        ],
        list: { columns: ["title", "employee", "category", "expense_date", "amount", "status"], filters: ["status", "category", "expense_date", "employee"], sort_field: "expense_date", sort_dir: "desc", page_size: 20, board_field: "status" },
        view: { history: true, related: false },
      },
      fields: [
        field("title", "Title", "text", "claim", { required: true }),
        field("employee", "Employee", "autocomplete", "claim", { required: true, source: "employee" }),
        choice("category", "Category", "claim", ["Travel", "Food", "Accommodation", "Fuel", "Office Supplies", "Other"]),
        field("expense_date", "Expense Date", "date", "claim", { required: true }),
        field("amount", "Amount", "number", "claim", { required: true, min: 0 }),
        choice("payment_mode", "Paid By", "claim", ["Cash", "Card", "UPI", "Bank Transfer"]),
        field("billable", "Billable to customer", "checkbox", "claim"),
        field("customer", "Customer", "autocomplete", "claim", { source: "customer", visible_when: when("billable", true) }),
        choice("status", "Status", "claim", ["Submitted", "Approved", "Rejected", "Paid"], [BLUE, GREEN, RED, PURPLE], { default: "Submitted" }),
        field("receipts", "Receipts", "attachment", "proof", { required: true, file_kind: "images_pdf", max_files: 5, max_size_mb: 5 }),
        field("remarks", "Remarks", "textarea", "proof"),
      ],
      actions: [
        button("Approve", "status", "Approved", "success", { visible_when: when("status", "Submitted") }),
        button("Reject", "status", "Rejected", "danger", { confirm: "Reject this claim?", visible_when: when("status", "Submitted") }),
        button("Mark Paid", "status", "Paid", "primary", { confirm: "Mark this claim as paid?", visible_when: when("status", "Approved") }),
      ],
    },
  },
  {
    key: "projects",
    name: "Project Tracker",
    description: "Projects with customer, manager, budget, tags and a milestone list, tracked by stage.",
    module: {
      name: "Projects",
      settings: {
        singular: "Project",
        description: "Delivery projects and their milestones",
        code_prefix: "PRJ-",
        sections: [
          { key: "overview", title: "Overview", columns: 2 },
          { key: "schedule", title: "Schedule & Budget", columns: 3 },
          { key: "milestones", title: "Milestones & Files", columns: 1 },
        ],
        list: { columns: ["project_name", "customer", "project_manager", "stage", "end_date", "budget"], filters: ["stage", "priority", "tags", "end_date"], sort_field: "end_date", sort_dir: "asc", page_size: 10, board_field: "stage" },
        view: { history: true, related: true },
      },
      fields: [
        field("project_name", "Project Name", "text", "overview", { required: true, unique: true }),
        field("customer", "Customer", "autocomplete", "overview", { source: "customer" }),
        field("project_manager", "Project Manager", "autocomplete", "overview", { source: "employee" }),
        choice("stage", "Stage", "overview", ["Planning", "In Progress", "On Hold", "Completed", "Cancelled"], [BLUE, AMBER, PURPLE, GREEN, GREY], { default: "Planning" }),
        choice("priority", "Priority", "overview", ["Low", "Medium", "High"], [GREY, BLUE, RED]),
        field("tags", "Tags", "multiselect", "overview", { options: ["Internal", "Client", "R&D", "Maintenance"] }),
        field("description", "Description", "textarea", "overview", { width: "full" }),
        field("start_date", "Start Date", "date", "schedule"),
        field("end_date", "End Date", "date", "schedule"),
        field("budget", "Budget", "number", "schedule", { min: 0 }),
        field("milestones", "Milestones", "repeater", "milestones", {
          max_rows: 20,
          subfields: [
            { key: "milestone", label: "Milestone", type: "text", required: true },
            { key: "due", label: "Due", type: "date" },
            { key: "state", label: "State", type: "select", options: ["Pending", "Done"] },
          ],
        }),
        field("files", "Files", "attachment", "milestones", { file_kind: "any", max_files: 10, max_size_mb: 10 }),
      ],
      actions: [
        button("Start Project", "stage", "In Progress", "primary", { visible_when: when("stage", "Planning") }),
        button("Put On Hold", "stage", "On Hold", "warning", { visible_when: when("stage", "In Progress") }),
        button("Complete", "stage", "Completed", "success", { confirm: "Mark this project as completed?", visible_when: when("stage", "In Progress") }),
      ],
    },
  },
  {
    key: "register",
    name: "Simple Register",
    description: "One clean section and a plain list. The quickest start for anything you want to keep track of.",
    module: {
      name: "Register",
      settings: {
        singular: "Entry",
        description: "",
        code_prefix: "",
        sections: [{ key: "main", title: "Details", columns: 2 }],
        list: { columns: ["title", "type", "owner", "entry_date"], filters: ["type", "entry_date"], sort_field: "", sort_dir: "desc", page_size: 10, board_field: "" },
        view: { history: true, related: false },
      },
      fields: [
        field("title", "Title", "text", "main", { required: true }),
        choice("type", "Type", "main", ["General", "Important"], [GREY, RED], { default: "General" }),
        field("owner", "Owner", "autocomplete", "main", { source: "employee" }),
        field("entry_date", "Date", "date", "main"),
        field("notes", "Notes", "textarea", "main", { width: "full" }),
      ],
      actions: [],
    },
  },
];

// Headline facts for a template card.
export const templateFacts = (template) => {
  const { module } = template;
  const sections = module.settings.sections.length;
  return [
    `${module.fields.filter((item) => !item.hidden).length} fields`,
    `${sections} section${sections > 1 ? "s" : ""}`,
    module.settings.list.board_field && "Board view",
    module.actions.length > 0 && `${module.actions.length} buttons`,
    module.settings.code_prefix && `Numbered ${module.settings.code_prefix}00001`,
    module.fields.some((item) => item.hidden) && "Hidden fields",
  ].filter(Boolean);
};
